import { getDatabase, ref, get, set } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { formatDateForStorage } from '../Helpers/DateUtils';
import { recordActivity } from './activityApi';

/**
 * Annual budget data stored at config/annualBudget/{year}
 */
export interface AnnualBudget {
    amount: number;        // Budget in SEK (positive integer)
    updatedBy: string;     // Firebase UID
    updatedWhen: string;   // "YYYY-MM-DD hh:mm:ss" Stockholm time
}

export class BudgetApi {
    private db = getDatabase();

    /**
     * Get the path for a year's budget
     */
    private getBudgetPath(year: number): string {
        return `config/annualBudget/${year}`;
    }

    /**
     * Validate budget amount
     */
    private validateAmount(amount: number): void {
        if (typeof amount !== 'number' || !Number.isInteger(amount)) {
            throw new Error('Ange ett heltal');
        }
        if (amount < 1) {
            throw new Error('Budget måste vara minst 1 kr');
        }
    }

    /**
     * Parse budget data from Firebase snapshot
     */
    private parseBudget(data: unknown): AnnualBudget | null {
        if (!data || typeof data !== 'object') return null;
        const d = data as Record<string, unknown>;

        // Validate required fields
        if (typeof d.amount !== 'number' ||
            typeof d.updatedBy !== 'string' ||
            typeof d.updatedWhen !== 'string') {
            console.warn('Invalid budget data: missing required fields');
            return null;
        }

        return {
            amount: d.amount,
            updatedBy: d.updatedBy,
            updatedWhen: d.updatedWhen,
        };
    }

    /**
     * Fetch budget for a specific year
     */
    async getBudget(year: number): Promise<AnnualBudget | null> {
        const budgetPath = this.getBudgetPath(year);
        const snapshot = await get(ref(this.db, budgetPath));

        if (!snapshot.exists()) {
            return null;
        }

        return this.parseBudget(snapshot.val());
    }

    /**
     * Set budget for a specific year
     * Requires admin permissions (enforced by database rules)
     */
    async setBudget(year: number, amount: number): Promise<void> {
        const user = getAuth().currentUser;
        if (!user) {
            throw new Error('Du måste vara inloggad för att sätta budget');
        }

        this.validateAmount(amount);

        const budgetPath = this.getBudgetPath(year);
        const budgetData: AnnualBudget = {
            amount,
            updatedBy: user.uid,
            updatedWhen: formatDateForStorage(new Date()),
        };

        await set(ref(this.db, budgetPath), budgetData);
        recordActivity('settings.updated', { targetKey: String(year) });
    }
}

// Export singleton instance
export const budgetApi = new BudgetApi();
